/* eslint-disable react/jsx-no-bind */
import {
	Button,
	Drawer,
	Modal,
	Space,
	Table,
	TablePaginationConfig,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import React, { useEffect, useState } from 'react'
import { getClientsAction, setClientPageSize } from 'slices/client'
import { ClientProfile, EdgePlatform, MobileNumber } from 'types'
import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import EditClientForm from 'container/EditClientForm'
import { Link } from 'react-router-dom'
import getPlatformLabel from 'utils/getPlatformLabel'
import archiveClients from 'api/clients/archiveClients'

const { Text } = Typography

/**
 * ClientsTable component props
 */

interface PropTypes {
	query: string
	active: boolean | undefined
	suspended: boolean | undefined
	onBoarding: boolean | undefined
	onBoardingStep: number | undefined
	plan: 'free' | 'paid' | undefined
	platformMode: 'live' | 'demo' | undefined
	archived: boolean
	fieldFilters: string | undefined
}

/**
 * Component for displaying clients table in ClientsPageLayout
 *
 * @param query - search query for clients
 * @returns Component ClientsTable with pagination
 */

const ClientsTable: React.FC<PropTypes> = ({
	query,
	active,
	suspended,
	onBoarding,
	onBoardingStep,
	plan,
	platformMode,
	archived,
	fieldFilters,
}) => {
	const dispatch = useAppDispatch()

	const { records, currentPage, totalCount, loading, size } = useAppSelector(
		state => state.client
	)
	const [drawer, setDrawer] = useState(false)
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
	const [updatingArchive, setUpdatingArchive] = useState(false)

	const [singleClient, setSingleClient] = useState<ClientProfile | null>(null)

	function openDrawer(data: ClientProfile) {
		setSingleClient(data)
		setDrawer(true)
	}
	function closeDrawer() {
		setDrawer(false)
	}

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	const listOptions = (page: number, pageSize = pagination.pageSize) => ({
		page,
		size: pageSize,
		query,
		active,
		suspended,
		onBoarding,
		onBoardingStep,
		plan,
		platformMode,
		archived,
		fieldFilters,
	})

	/**
	 * Fetch clients on mount and on query change
	 */

	useEffect(() => {
		setSelectedRowKeys([])
		dispatch(getClientsAction(listOptions(1)))
	}, [
		query,
		active,
		suspended,
		onBoarding,
		onBoardingStep,
		plan,
		platformMode,
		archived,
		fieldFilters,
	])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */
	const handlePageChange = (page: TablePaginationConfig) => {
		dispatch(setClientPageSize(page.pageSize || size))
		dispatch(
			getClientsAction(listOptions(page.current || 1, page.pageSize || size))
		)
	}

	const refreshList = () => {
		dispatch(getClientsAction(listOptions(currentPage, size)))
	}

	const confirmArchiveChange = () => {
		const ids = selectedRowKeys.map(String)
		const count = ids.length
		if (!count) return
		const restoring = archived
		Modal.confirm({
			title: restoring
				? `Restore ${count} client${count === 1 ? '' : 's'}?`
				: `Archive ${count} client${count === 1 ? '' : 's'}?`,
			content: restoring
				? 'They will return to the main clients list. Suspension caused by archiving is lifted. Accounts that were already suspended stay suspended.'
				: 'Their accounts will be suspended and hidden from the main clients list. You can restore them from the Archived view.',
			okText: restoring ? 'Restore' : 'Archive',
			okButtonProps: { danger: !restoring },
			onOk: async () => {
				try {
					setUpdatingArchive(true)
					await archiveClients(ids, !restoring)
					message.success(
						restoring
							? `${count} client${count === 1 ? '' : 's'} restored`
							: `${count} client${count === 1 ? '' : 's'} archived`
					)
					setSelectedRowKeys([])
					refreshList()
				} finally {
					setUpdatingArchive(false)
				}
			},
		})
	}

	/**
	 * Columns for the table of clients
	 */
	const columns = [
		{
			title: 'Name',
			dataIndex: 'organizationName',
			render: (name: string, record: ClientProfile) => (
				<Space direction='vertical' size={0}>
					<Link to={`/clients/${record._id}`}>{name}</Link>
					<Text type='secondary'>{record.clientId}</Text>
				</Space>
			),
		},
		{
			title: 'Email',
			dataIndex: 'email',
		},
		{
			title: 'Mobile',
			dataIndex: 'mobileNumber',
			render: (mobileNumber: MobileNumber) =>
				mobileNumber ? `+${mobileNumber.code} ${mobileNumber.mobile}` : '-',
		},
		{
			title: 'Platform',
			dataIndex: 'selectedEDGEPlatform',
			render: (platform: EdgePlatform | undefined) =>
				platform ? getPlatformLabel(platform) : '-',
		},
		{
			title: 'Mode',
			dataIndex: 'platformMode',
			render: (type: 'DEMO' | 'LIVE' | undefined) => (
				<Tag color={type === 'DEMO' ? 'error' : 'success'}>
					{type === 'DEMO' ? 'Demo' : 'Live'}
				</Tag>
			),
		},
		{
			title: 'Plan',
			dataIndex: 'isFreePlan',
			render: (type: boolean) => (
				<Tag color={type ? 'processing' : 'success'}>{type ? 'Free' : 'Paid'}</Tag>
			),
		},
		{
			title: 'Onboarded',
			dataIndex: 'is_account_setup',
			render: (onboarded: boolean) => (
				<Tag color={onboarded ? 'success' : undefined}>
					{onboarded ? 'Yes' : 'No'}
				</Tag>
			),
		},
		{
			title: 'Status',
			key: 'status',
			render: (_: unknown, record: ClientProfile) => (
				<Space size={4} wrap>
					<Tag color={record.active ? 'success' : undefined}>
						{record.active ? 'Active' : 'Inactive'}
					</Tag>
					{record.accountSuspended && <Tag color='error'>Suspended</Tag>}
					{record.archived && <Tag>Archived</Tag>}
				</Space>
			),
		},
		{
			title: 'Action',
			key: 'action',
			dataIndex: '_id',
			render: (id: string, data: ClientProfile) => (
				<Space>
					<Tooltip placement='bottomLeft' title='View'>
						<Link to={`/clients/${id}`}>
							<Button type='primary' icon={<EyeOutlined />} />
						</Link>
					</Tooltip>
					<Button
						type='primary'
						icon={<EditOutlined />}
						onClick={() => openDrawer(data)}
					/>
				</Space>
			),
		},
	]

	return (
		<>
			{selectedRowKeys.length > 0 && (
				<Space style={{ marginBottom: 12 }}>
					<Text>{selectedRowKeys.length} selected</Text>
					<Button
						danger={!archived}
						type={archived ? 'primary' : 'default'}
						loading={updatingArchive}
						onClick={confirmArchiveChange}
					>
						{archived ? 'Restore' : 'Archive'}
					</Button>
					<Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
				</Space>
			)}
			<Table
				columns={columns}
				rowKey={record => record._id}
				dataSource={records}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				loading={loading}
				onChange={handlePageChange}
				scroll={{ x: 'fit-content' }}
				rowSelection={{
					selectedRowKeys,
					onChange: keys => setSelectedRowKeys(keys),
					preserveSelectedRowKeys: true,
				}}
				locale={{
					emptyText: archived ? 'No archived clients' : 'No clients',
				}}
			/>
			<Drawer
				title='Edit Client'
				placement='right'
				width='70rem'
				closable
				visible={drawer}
				onClose={closeDrawer}
				destroyOnClose
			>
				{singleClient !== null && (
					<EditClientForm closeDrawer={closeDrawer} singleClient={singleClient} />
				)}
			</Drawer>
		</>
	)
}

export default ClientsTable
